<template>
  <div class="webview-login-container">
    <!-- 只显示嵌入模式的登录按钮 -->
    <div class="webview-embedded-wrapper">
      <div class="webview-login-wrapper">
        <!-- Google登录 -->
        <button 
          v-if="loginType === 'google'"
          :class="['webview-login-btn', 'webview-google-btn', { 'mobile': isMobile }]" 
          @click="handleGoogleLogin"
          :disabled="loading"
        >
          <img src="@/assets/icon/google.png" alt="Google" class="webview-btn-icon" />
          <span v-if="!loading">Continue with Google</span>
          <span v-else>Signing in...</span>
        </button>

        <!-- Facebook登录 -->
        <button 
          v-if="loginType === 'facebook'"
          :class="['webview-login-btn', 'webview-facebook-btn', { 'mobile': isMobile }]" 
          @click="handleFacebookLogin"
          :disabled="loading"
        >
          <img src="@/assets/icon/facebook.png" alt="Facebook" class="webview-btn-icon" />
          <span v-if="!loading">Continue with Facebook</span>
          <span v-else>Signing in...</span>
        </button>

        <!-- Apple登录 -->
        <button 
          v-if="loginType === 'apple'"
          :class="['webview-login-btn', 'webview-apple-btn', { 'mobile': isMobile }]" 
          @click="handleAppleLogin"
          :disabled="loading"
        >
          <svg class="webview-btn-icon webview-apple-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
          <span v-if="!loading">Continue with Apple</span>
          <span v-else>Signing in...</span>
        </button>

        <!-- 加载状态 -->
        <div v-if="loading" class="webview-loading-container">
          <div class="webview-loading-spinner"></div>
          <p>Processing login...</p>
        </div>
      </div>

      <!-- 错误信息 -->
      <div v-if="errorMessage" class="webview-error-message">
        <p>{{ errorMessage }}</p>
        <button @click="retryLogin" class="webview-retry-btn">Try Again</button>
      </div>
    </div>
  </div>
</template>

<script>
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider, appleProvider } from '@/firebase.js';
import { postGateway } from '@/request';
import bus from '@/utils/bus.js';
import { FACEBOOK_CONFIG, FACEBOOK_FIELDS } from '@/utils/facebook.js';

export default {
  name: 'FirebaseLoginWebview',
  data() {
    return {
      loading: false,
      errorMessage: '',
      loginType: null,
      isMobile: false
    }
  },
  mounted() {
    // 监听来自父窗口的消息
    window.addEventListener('message', this.handleParentMessage);
    
    // 检查URL参数
    this.checkUrlParams();
    
    // 初始化Facebook SDK（如果需要）
    this.initFacebookSDKIfNeeded();
    
    // 延迟发送webview加载完成消息，确保loginType已设置
    setTimeout(() => {
      this.sendWebviewLoadedMessage();
    }, 500);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleParentMessage);
  },
  methods: {
    // 检查URL参数
    checkUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const loginType = urlParams.get('type');
      const isMobile = urlParams.get('mobile');
      
      if (loginType) {
        this.loginType = loginType;
      }
      
      if (isMobile === 'true') {
        this.isMobile = true;
      } else if (isMobile === 'false') {
        this.isMobile = false;
      } else {
        // 如果没有传递mobile参数，使用默认检测
        this.isMobile = window.innerWidth < 950;
      }
    },

    // 发送webview加载完成消息
    sendWebviewLoadedMessage() {
      console.log('准备发送webview加载完成消息, loginType:', this.loginType);
      
      // 确保loginType存在
      if (!this.loginType) {
        console.warn('loginType未设置，延迟发送消息');
        setTimeout(() => {
          this.sendWebviewLoadedMessage();
        }, 200);
        return;
      }
      
      // 延迟发送，确保组件完全加载
      setTimeout(() => {
        if (window.parent && window.parent !== window) {
          const message = {
            type: 'WEBVIEW_LOADED',
            loginType: this.loginType,
            timestamp: Date.now()
          };
          
          window.parent.postMessage(message, '*');
          console.log('Webview loaded message sent:', message);
        } else {
          console.warn('没有找到父窗口，无法发送消息');
        }
      }, 100);
    },

    // 初始化Facebook SDK（如果需要）
    initFacebookSDKIfNeeded() {
      // 只有在Facebook登录时才初始化SDK
      if (this.loginType === 'facebook') {
        this.initFacebookSDK();
      }
    },

    // 初始化Facebook SDK
    initFacebookSDK() {
      // 检查Facebook SDK是否已加载
      if (window.FB) {
        this.setupFacebookLogin();
        return;
      }

      // 动态加载Facebook SDK
      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      
      script.onload = () => {
        this.setupFacebookLogin();
      };
      
      document.head.appendChild(script);
    },

    // 设置Facebook登录
    setupFacebookLogin() {
      window.FB.init({
        appId: FACEBOOK_CONFIG.APP_ID,
        cookie: true,
        xfbml: true,
        version: FACEBOOK_CONFIG.VERSION
      });

      // 设置登录状态检查
      window.FB.getLoginStatus((response) => {
        console.log('Facebook login status:', response);
      });
    },

    // 使用Facebook SDK登录
    loginWithFacebookSDK() {
      return new Promise((resolve, reject) => {
        window.FB.login((response) => {
          if (response.authResponse) {
            console.log('Facebook login successful:', response);
            this.handleFacebookResponse(response);
            resolve(response);
          } else {
            console.log('Facebook login cancelled or failed');
            reject(new Error('Facebook login was cancelled'));
          }
        }, {
          scope: FACEBOOK_CONFIG.SCOPE,
          return_scopes: FACEBOOK_CONFIG.RETURN_SCOPES
        });
      });
    },

    // 处理Facebook登录响应
    async handleFacebookResponse(response) {
      try {
        const { accessToken, userID } = response.authResponse;
        
        // 获取用户信息
        const userInfo = await this.getFacebookUserInfo();
        
        // 检查邮箱是否存在
        if (!userInfo.email) {
          this.errorMessage = 'Facebook login failed. Please try others.';
          this.loading = false;
          return;
        }
        
        // 发送到后端验证
        const loginData = await this.sendUserToBackend({
          uid: userID,
          email: userInfo.email,
          displayName: userInfo.name,
          photoURL: userInfo.picture?.data?.url || '',
          accessToken,
          provider: 'facebook'
        });

        // 发送成功消息给父窗口
        this.sendSuccessToParent(loginData);
        
      } catch (error) {
        console.error('Facebook login processing error:', error);
        this.loading = false; // 确保在错误时停止loading
        throw error;
      }
    },

    // 获取Facebook用户信息
    getFacebookUserInfo() {
      return new Promise((resolve, reject) => {
        window.FB.api('/me', {
          fields: FACEBOOK_FIELDS
        }, (response) => {
          if (response && !response.error) {
            resolve(response);
          } else {
            reject(new Error(response.error?.message || 'Failed to get user info'));
          }
        });
      });
    },

    // 处理来自父窗口的消息
    handleParentMessage(event) {
      // 验证来源域名
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === 'REQUEST_LOGIN') {
        const { loginType } = event.data;
        this.loginType = loginType;
        
        if (loginType === 'google') {
          this.handleGoogleLogin();
        } else if (loginType === 'facebook') {
          this.handleFacebookLogin();
        } else if (loginType === 'apple') {
          this.handleAppleLogin();
        }
      }
    },

    // Google登录
    async handleGoogleLogin() {
      this.startLoading('google');
      
      try {
        const result = await signInWithPopup(auth, googleProvider);
        const user = result.user;
        
        // 检查邮箱是否存在，从多个来源获取邮箱
        let email = user.email;
        if (!email && user.providerData && user.providerData.length > 0) {
          email = user.providerData[0].email;
        }
        if (!email && user.reloadUserInfo && user.reloadUserInfo.providerUserInfo && user.reloadUserInfo.providerUserInfo[0]) {
          email = user.reloadUserInfo.providerUserInfo[0].email;
        }
        
        if (!email) {
          this.errorMessage = 'Email is required for Google login. Please ensure your Google account has an email address.';
          this.loading = false;
          return;
        }
        
        // 获取ID Token和Access Token
        const idToken = await user.getIdToken();
        const accessToken = result._tokenResponse?.oauthAccessToken;
        
        // 发送到后端验证
        const loginData = await this.sendUserToBackend({
          uid: user.uid,
          email: email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          idToken,
          accessToken,
          provider: 'google'
        });

        // 发送成功消息给父窗口
        this.sendSuccessToParent(loginData);
        
      } catch (error) {
        console.error('Google login error:', error);
        this.handleLoginError(error);
      }
    },

    // Facebook登录
    async handleFacebookLogin() {
      this.startLoading('facebook');
      
      try {
        // 使用Facebook SDK进行登录
        await this.loginWithFacebookSDK();
      } catch (error) {
        console.error('Facebook login error:', error);
        this.handleLoginError(error);
      }
    },

    // Apple登录
    async handleAppleLogin() {
      this.startLoading('apple');
      
      try {
        // 生成nonce用于安全验证
        const nonce = this.generateNonce();
        appleProvider.setCustomParameters({
          nonce: nonce
        });

        const result = await signInWithPopup(auth, appleProvider);
        const user = result.user;
        
        console.log('Apple login successful:', user);
        
        // 检查邮箱是否存在，从多个来源获取邮箱
        let email = user.email;
        if (!email && user.providerData && user.providerData.length > 0) {
          email = user.providerData[0].email;
        }
        if (!email && user.reloadUserInfo && user.reloadUserInfo.providerUserInfo?.[0]?.email) {
          email = user.reloadUserInfo.providerUserInfo?.[0]?.email;
        }
        
        // Apple登录可能没有邮箱，这是正常的
        if (!email) {
          console.log('Apple login without email - this is normal for Apple Sign In');
          // 使用uid作为标识符
          email = user.uid + '@apple.privaterelay.appleid.com';
        }
        
        // 获取Apple ID Token
        const idToken = await user.getIdToken();
        
        // 发送到后端验证
        const loginData = await this.sendUserToBackend({
          uid: user.uid,
          email: email,
          displayName: user.displayName || 'Apple User',
          photoURL: user.photoURL || '',
          idToken,
          provider: 'apple'
        });

        // 发送成功消息给父窗口
        this.sendSuccessToParent(loginData);
        
      } catch (error) {
        console.error('Apple login error:', error);
        this.handleLoginError(error);
      }
    },

    // 发送用户数据到后端
    async sendUserToBackend(userData) {
      let endpoint;
      if (userData.provider === 'google') {
        endpoint = '/oauth/appGoogleLogin';
      } else if (userData.provider === 'facebook') {
        endpoint = '/oauth/appGoogleLogin';
      } else if (userData.provider === 'apple') {
        endpoint = '/oauth/appGoogleLogin';
      }
      
      const requestData = {
        idToken: userData.idToken,
        email: userData.email,
        firstName: userData.displayName,
        photoUrl: userData.photoURL,
        render: userData.provider,
        type: 'web'
      };

      // 根据不同的provider添加特定的ID字段
      if (userData.provider === 'google') {
        requestData.googleId = userData.uid;
        requestData.accessToken = userData.accessToken;
      } else if (userData.provider === 'facebook') {
        requestData.facebookId = userData.uid;
        requestData.accessToken = userData.accessToken;
      } else if (userData.provider === 'apple') {
        requestData.appleId = userData.uid;
      }

      const response = await postGateway({
        url: endpoint,
        method: 'POST',
        data: requestData
      });

      if (response.code === 200) {
        return response.data;
      } else {
        throw new Error(response.msg || 'Login failed');
      }
    },

    // 发送成功消息给父窗口
    sendSuccessToParent(loginData) {
      // 停止loading状态
      this.loading = false;
      
      // 保存登录信息到localStorage
      localStorage.setItem('token', loginData.token);
      localStorage.setItem('refreshToken', loginData.refreshToken);
      localStorage.setItem('clientId', loginData.clientId);
      localStorage.setItem('aeClient', loginData.clientId);
      localStorage.setItem('userInfo', JSON.stringify(loginData));
      
      // 通知header登录成功
      bus.$emit('login');
      
      const message = {
        type: 'LOGIN_SUCCESS',
        loginType: this.loginType, // 添加登录类型
        data: {
          token: loginData.token,
          refreshToken: loginData.refreshToken,
          clientId: loginData.clientId,
          userInfo: loginData
        }
      };
      
      // 发送给父窗口
      if (window.parent && window.parent !== window) {
        console.log('发送消息给父窗口:', message);
        window.parent.postMessage(message, '*');
      }
      
      // 也发送给opener（如果是从弹窗打开的）
      if (window.opener) {
        console.log('发送消息给opener:', message);
        window.opener.postMessage(message, '*');
      }
    },

    // 开始加载
    startLoading(type) {
      this.loading = true;
      this.errorMessage = '';
      this.loginType = type;
    },

    // 处理登录错误
    handleLoginError(error) {
      this.loading = false;
      
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'An account already exists with this email using a different login method.';
      } else if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Login was cancelled. Please try again.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked. Please allow popups and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      this.errorMessage = errorMessage;
      
      // 发送错误消息给父窗口
      const message = {
        type: 'LOGIN_ERROR',
        loginType: this.loginType, // 添加登录类型
        error: errorMessage
      };
      
      if (window.parent && window.parent !== window) {
        console.log('发送错误消息给父窗口:', message);
        window.parent.postMessage(message, '*');
      }
      
      if (window.opener) {
        console.log('发送错误消息给opener:', message);
        window.opener.postMessage(message, '*');
      }
    },

    // 重试登录
    retryLogin() {
      this.errorMessage = '';
      if (this.loginType === 'google') {
        this.handleGoogleLogin();
      } else if (this.loginType === 'facebook') {
        this.handleFacebookLogin();
      } else if (this.loginType === 'apple') {
        this.handleAppleLogin();
      }
    },

    // 生成nonce用于Apple登录安全验证
    generateNonce() {
      const array = new Uint8Array(32);
      crypto.getRandomValues(array);
      return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    }
  }
}
</script>

<style scoped>
.webview-login-container {
  min-height: auto;
  background: transparent;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.webview-embedded-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: auto;
  background: transparent;
  padding: 0;
  margin: 0;
}

.webview-embedded-wrapper .webview-login-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0;
  margin: 0;
}

.webview-embedded-wrapper .webview-login-btn {
  width: 100%;
  height: 100%;
  max-width: 300px;
  min-width: 300px;
  min-height: 48px;
  font-size: 16px;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

/* 移动端样式 */
.webview-embedded-wrapper .webview-login-btn.mobile {
  min-height: 44px;
  font-size: 14px;
}

.webview-embedded-wrapper .webview-btn-icon {
  width: 20px;
  height: 20px;
  margin-right: 12px;
}

.webview-embedded-wrapper .webview-loading-container {
  margin-top: 10px;
}

.webview-embedded-wrapper .webview-error-message {
  margin-top: 10px;
  padding: 10px;
  font-size: 12px;
}


.webview-login-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 48px;
}

.webview-login-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.webview-google-btn {
  background: rgb(242, 242, 242);
  color: #333;
}


.webview-facebook-btn {
  background: #1877f2;
  color: white;
}

.webview-apple-btn {
  background: #000;
  color: white;
}

.webview-btn-icon {
  width: 20px;
  height: 20px;
  margin-right: 12px;
}

.webview-loading-container {
  text-align: center;
  color: white;
  margin-top: 20px;
}

.webview-loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: webview-spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes webview-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.webview-error-message {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  color: white;
  margin-top: 20px;
  max-width: 400px;
}

.webview-error-message p {
  margin-bottom: 16px;
  font-size: 14px;
}

.webview-retry-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}


</style>
